import { ArticleCard } from './ArticleCard';
import { ArticleCardSkeleton } from './ArticleCardSkeleton';
import { ArticleCardBlockProps, ArticleCardQueryParams } from './contracts';
import { useArticlesQuery, ArticlesQueryArgs } from '@data/articles/useArticlesQuery';

type ArticleSearchParams = ArticleCardBlockProps['searchParams'];

// Resolve the single-article query from the CMS config (falling back to the
// URL `tags` param, mirroring the Highlighted card). Kept out of the component
// so its branchy null-coalescing doesn't inflate the component's complexity.
const buildArticleCardQueryArgs = (
  queryParams: ArticleCardQueryParams,
  searchParams: ArticleSearchParams
): ArticlesQueryArgs => ({
  limit: 1,
  tags: (queryParams.tags || (searchParams?.tags as string)) ?? '',
  tagsMatchMode: queryParams.tagsMatchMode ?? undefined,
  sort: queryParams.sort,
  order: queryParams.order,
});

/**
 * CMS wrapper for the standalone "Article Card" component. Fetches the single
 * top-matching article for the configured tag query and renders the shared
 * `ArticleCard` in the chosen variant. The rendering component is reused as-is
 * so its layouts, truncation and styling stay in lockstep with every other
 * surface that uses `ArticleCard`.
 */
export function ArticleCardBlock({
  queryParams,
  searchParams,
  variant,
  imageObjectFit,
  size,
  style,
  showAuthor = false,
  showDate = true,
  showImage = true,
  showTag = true,
  showSummary = false,
  prioritize = false,
}: ArticleCardBlockProps) {
  const { data, isLoading, error } = useArticlesQuery(buildArticleCardQueryArgs(queryParams, searchParams));

  const article = data?.items[0];

  if (isLoading) {
    return (
      <ArticleCardSkeleton
        variant={variant}
        showImage={showImage}
        showTag={showTag}
        showDate={showDate}
        showAuthor={showAuthor}
        showSummary={showSummary}
      />
    );
  }

  if (!data || !article || error) {
    return null;
  }

  return (
    <ArticleCard
      article={article}
      variant={variant}
      imageObjectFit={imageObjectFit}
      size={size}
      style={style}
      showAuthor={showAuthor}
      showDate={showDate}
      showImage={showImage}
      showTag={showTag}
      showSummary={showSummary}
      prioritize={prioritize}
    />
  );
}
